<template>
  <div class="map-container">
    <div class="map-content">
      <div :id="mapId" class="ol-container"></div>

      <!-- 加载动画 -->
      <div v-if="loading" class="map-loading-mask">
        <a-spin size="large" :tip="loadingTip" />
      </div>

      <!-- 查询组件 (位于底图控制左侧) -->
      <map-query :marker-data-groups="markerDataGroups" :line-source="lineSource" @select="handleSearchResultSelect" />

      <!-- 底部工具栏：包含全屏和底图切换 -->
      <base-layer-control :value="selectedValue" :map-layer-configs="mapLayerConfigs" :is-full-screen="isFullScreen"
        :show-layers="showLayers" :is-adding="isAdding" :is-line-adding="isLineAdding" :is-measuring="isMeasuring"
        :is-measuring-surface="isMeasuringSurface" @change="handleLayerChange" @toggle-fullscreen="toggleFullScreen"
        @toggle-layers="showLayers = !showLayers" @add-point="handleAddPoint" @add-line="handleAddLine"
        @toggle-measure="handleToggleMeasure" @toggle-measure-surface="handleToggleMeasureSurface" />

      <!-- 图层显示/隐藏控制列表 -->
      <layer-control v-if="showLayers" :mapping-table="mappingTable" :visible-layers="visibleLayers"
        @change="handleLayerVisibilityChange" />

      <!-- 详情面板 -->
      <info-panel ref="infoPanel" :mapping-table="mappingTable" @close="handleInfoPanelClose" @edit="handleEdit"
        @delete="handleDelete" @move-start="handleMovePointStart" @move-cancel="handleInfoPanelMoveCancel" />

      <!-- 编辑表单 -->
      <edit-form ref="editForm" @save="handleEditSave" @close="clearEditHighlight" @loading="handleFormLoading" />

      <!-- 移动点确认弹出窗口 -->
      <div ref="movePopup" class="move-popup" v-show="moveConfirmVisible">
        <div class="move-popup-content">
          <div class="move-popup-text">确认移动到该位置？</div>
          <div class="move-popup-actions">
            <a-button size="small" @click="cancelMoveAction">取消</a-button>
            <a-button type="primary" size="small" @click="confirmMoveAction">确定</a-button>
          </div>
        </div>
      </div>

      <!-- 新增模式下的状态切换器 -->
      <div v-if="isAdding || isLineAdding" class="add-mode-toggle">
        <a-switch checked-children="新增模式" un-checked-children="查看模式" :checked="addModeType === 'add'"
          @change="(checked) => addModeType = checked ? 'add' : 'view'" />
      </div>
    </div>
  </div>
</template>

<script>
import 'ol10/ol.css';
import Map from 'ol10/Map';
import View from 'ol10/View';
import Overlay from 'ol10/Overlay';
import XYZ from 'ol10/source/XYZ';
import * as olProj from 'ol10/proj';
import { getUUID } from '@/utils/util';
import { LonLat } from '@/store/mutation-types';
import storage from 'store';
import { defaults as defaultInteractions } from 'ol10/interaction'
import TileLayer from 'ol10/layer/Tile';
import VectorLayer from 'ol10/layer/Vector';
import WebGLPointsLayer from 'ol10/layer/WebGLPoints';
import WebGLVectorLayer from 'ol10/layer/WebGLVector';
import VectorSource from 'ol10/source/Vector';
import Feature from 'ol10/Feature';
import Point from 'ol10/geom/Point';
import LineString from 'ol10/geom/LineString';
import Polygon from 'ol10/geom/Polygon';
import { Style, Stroke, Fill, Circle as CircleStyle, Text } from 'ol10/style';
import { fromLonLat } from 'ol10/proj';
import { calculateDistance, calculateTotalDistance, formatDistance, calculatePolygonArea, formatArea } from '@/utils/geoUtils';
import { registerGCJ02, getSpecialTileUrl } from './module/mapHelper';
import MappingTable, { parseMappingData } from './MappingTable';
import LayerControl from './module/LayerControl';
import InfoPanel from './module/InfoPanel';
import BaseLayerControl from './module/BaseLayerControl';
import MapQuery from './module/MapQuery';
import EditForm from './module/EditForm';
import formConfig from './module/formConfig';
import { strategyMap } from './module/updateHandler';
import { message } from 'ant-design-vue';


import { getImageBase64, getCachedImageBase64 } from '@/utils/imageUtils';

export default {
  name: 'EditingAmap',
  components: {
    LayerControl,
    InfoPanel,
    BaseLayerControl,
    MapQuery,
    EditForm
  },
  data () {
    return {
      markerUrl: '/pipe-network/gis/pipe-networks/get/point',
      lineUrl: '/pipe-network/gis/pipe-lines/get/line',
      pointDetailUrl: '/pipe-network/gis/pipe-networks/work/',
      lineDetailUrl: '/pipe-network/gis/pipe-lines/',
      pointDeleteUrl: '/pipe-network/gis/pipe-networks/del/point',
      lineDeleteUrl: '/pipe-network/gis/pipe-lines/del/line',
      pointConverterUrl: '/pipe-network/gis/pipe-networks/converter/point',
      mapId: getUUID(),
      map: null,
      lineLayer: null,
      lineSource: null,
      highlightLayer: null,
      highlightSource: null,
      editHighlightLayer: null,
      editHighlightSource: null,
      markerLayers: {}, // 存储按类型分的 WebGL 图层
      markerSources: {}, // 存储按类型分的 Source
      markerDataGroups: {}, // 存储按类型分组的点位要素，实现延迟加载
      baseLayer: null,
      selectedValue: 'layer1',
      mappingTable: MappingTable,
      visibleLayers: Object.keys(MappingTable).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
      mapLayerConfigs: {
        layer1: {
          name: '高德矢量',
          url: 'https://wprd0{1-4}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&style=7&x={x}&y={y}&z={z}',
          projection: 'GCJ-02',
          maxZoom: 18
        },
        layer2: {
          name: '高德影像',
          url: 'https://webst01.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}',
          projection: 'GCJ-02',
          maxZoom: 16
        },
        layer3: {
          name: '天地影像',
          url: 'https://t7.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=485cd3a62955d7a962a5c0d64d4949f2',
          projection: 'EPSG:3857',
          maxZoom: 18
        },
        layer4: {
          name: '必应地图',
          url: 'http://dynamic.t0.tiles.ditu.live.com/comp/ch/',
          urlEnd: '?it=G,VE,BX,L,LA&mkt=zh-cn,syr&n=z&og=111&ur=CN',
          useTileUrlFunction: true,
          projection: 'GCJ-02',
          maxZoom: 18
        },
        layer5: {
          name: '星图地图',
          url: 'https://api.open.geovisearth.com/pj/base/v1/img/',
          urlEnd: '?format=webp&tmsIds=w&token=131c96a153996bd9fb4ef1f9ffcb4ccf',
          useTileUrlFunction: true,
          projection: 'EPSG:3857',
          maxZoom: 18
        }
      },
      selectedFeature: null,
      loading: false,
      isFullScreen: false,
      showLayers: true,
      isAdding: false,
      isLineAdding: false,
      isMovingPoint: false,
      isMeasuring: false,
      isMeasuringSurface: false,
      moveConfirmVisible: false,
      // 测距相关状态 (参考 gisMap.vue)
      measureDistancePoints: [],
      measureDistanceSource: null,
      measureDistanceLayer: null,
      measureDistanceTotalLabel: null,
      // 测面相关状态 (参考 gisMap.vue)
      measureSurfacePoints: [],
      measureSurfaceSource: null,
      measureSurfaceLayer: null,
      measureSurfacePolygon: null,
      measureSurfaceLabel: null,
      tempMoveCoords: null,
      moveOverlay: null,
      lineAddPoints: [], // 存储新增连线时的点信息
      addModeType: 'add', // 新增模式下的操作类型：add (新增选点), view (查看现有要素)
      drawLayer: null,
      drawSource: null,
      loadingTip: '正在加载管网数据...'
    }
  },
  mounted () {
    // 配置消息提示挂载到当前组件，确保全屏模式下可见，并调整显示位置
    message.config({
      getContainer: () => this.$el,
      top: '100px' // 将位置调低一点（默认通常是 24px）
    });

    this.initMap();
    this.loadAllData();
    // 监听全屏变化
    document.addEventListener('fullscreenchange', this.handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullScreenChange);
  },
  beforeDestroy () {
    // 还原消息提示的全局挂载点和默认位置
    message.config({
      getContainer: () => document.body,
      top: '24px' // 还原回默认值
    });

    // 移除监听
    document.removeEventListener('fullscreenchange', this.handleFullScreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullScreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullScreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullScreenChange);
  },
  methods: {
    // 统一加载数据的方法
    async loadAllData () {
      this.loadingTip = '正在加载配置数据...';
      this.loading = true;
      try {
        // 1. 先加载配置映射表（内部会预加载图标）
        await this.getMappingTableData();

        // 2. 同时请求点位和管线数据
        this.loadingTip = '正在加载管网数据...';
        await Promise.all([
          this.getMarkerData(),
          this.getLineData()
        ]);
      } catch (error) {
        console.error('加载数据失败:', error);
      } finally {
        this.loading = false;
      }
    },
    // 获取动态配置映射表
    async getMappingTableData () {
      try {
        const res = await this.$http.get('/manager/gis/legend/list');
        // 极致兼容解析逻辑
        let list = [];
        if (res.data && res.data.data && Array.isArray(res.data.data.list)) {
          list = res.data.data.list;
        } else if (res.data && Array.isArray(res.data.list)) {
          list = res.data.list;
        } else if (Array.isArray(res.data)) {
          list = res.data;
        }

        if (list.length > 0) {
          const newMapping = parseMappingData(list);
          this.mappingTable = { ...newMapping }; // 触发响应式更新

          // 更新可见性列表，保留已有的可见性状态，新增的默认开启
          const newVisible = { ...this.visibleLayers };
          Object.keys(this.mappingTable).forEach(key => {
            if (this.mappingTable[key].defaultShow === 1) {
              newVisible[key] = true;
            }
          });
          this.visibleLayers = newVisible;

          // 同步更新线图层样式（颜色和可见性）
          this.updateLineLayerStyle();

          // 在获取映射表后立即预加载所有图标（图层控制显示时使用）
          await this.preloadAllIcons();
        }
      } catch (err) {
        console.error('获取图例配置失败:', err);
      }
    },
    /**
     * 预加载所有图标图片
     * 在获取映射表后立即预加载，避免显示黑色点
     * 使用 getImageBase64 预加载并存储到缓存中
     * 图层控制显示时会优先使用缓存
     */
    async preloadAllIcons () {
      const iconUrls = new Set();

      // 收集所有图标的 URL
      Object.keys(this.mappingTable).forEach(key => {
        const mapping = this.mappingTable[key];
        if (mapping && mapping.icon) {
          const iconUrl = mapping.icon;
          // 只预加载有效的 HTTP/HTTPS 图片（排除颜色图标和 base64）
          if (iconUrl &&
            typeof iconUrl === 'string' &&
            !iconUrl.startsWith('color:') &&
            !iconUrl.startsWith('data:image') &&
            (iconUrl.startsWith('http://') || iconUrl.startsWith('https://'))) {
            iconUrls.add(iconUrl);
          }
        }
      });

      if (iconUrls.size === 0) {
        return Promise.resolve();
      }

      console.log(`开始预加载 ${iconUrls.size} 个图标并存储到缓存...`);

      // 使用 getImageBase64 并行预加载所有图片，会自动存储到 imageCache
      // 这样后续使用时可以直接从缓存获取，无需再次请求
      const preloadPromises = Array.from(iconUrls).map(async (iconUrl) => {
        try {
          await getImageBase64(iconUrl);
          console.log(`图标预加载并缓存成功: ${iconUrl}`);
        } catch (err) {
          console.warn(`图标预加载失败: ${iconUrl}`, err);
          // 即使失败也继续，不阻塞后续流程
        }
      });

      await Promise.all(preloadPromises);
      console.log('所有图标预加载完成，已存储到缓存');
    },
    initMap () {
      try {
        console.log('开始初始化地图...');
        const targetElement = document.getElementById(this.mapId);
        if (!targetElement) return;

        const center = this.getInitialCenter();

        // 1. 初始化 OpenLayers (顶层)
        this.map = new Map({
          controls: [],
          interactions: defaultInteractions({
            doubleClickZoom: false // 禁用双击放大
          }),
          view: new View({
            center: fromLonLat(center),
            // projection: 'EPSG:4326',
            zoom: 14,
            maxZoom: 28,
            minZoom: 1,
            constrainResolution: true, // 限制分辨率为整数缩放级别，提升性能
            smoothResolutionConstraint: false // 禁用平滑分辨率约束，加快缩放
          }),
          target: this.mapId,
          layers: [],
          loadTilesWhileAnimating: true, // 动画时继续加载瓦片
          loadTilesWhileInteracting: true // 交互时继续加载瓦片
        });

        this.createBaseLayer();
        this.createLineLayer(); // 初始化管线图层
        this.createHighlightLayer(); // 初始化高亮图层
        this.createEditHighlightLayer(); // 初始化编辑高亮图层
        this.createDrawLayer(); // 初始化绘制图层
        this.initMoveOverlay(); // 初始化移动点确认弹窗
        this.initMeasureDistanceLayer(); // 初始化测距图层 (参考 gisMap.vue)
        this.initMeasureSurfaceLayer(); // 初始化测面图层 (参考 gisMap.vue)


        // 4. 监听点击事件
        this.map.on('click', (evt) => {
          this.handleMapClick(evt);
        });

        // 5. 监听鼠标移动，改变指针样式
        this.map.on('pointermove', (evt) => {
          if (evt.dragging) return;
          // 仅在新增点或新增线模式且处于“新增类型”时，或处于移动点模式时显示十字准星
          if (((this.isAdding || this.isLineAdding) && this.addModeType === 'add') || this.isMovingPoint) {
            this.map.getTargetElement().style.cursor = 'crosshair';
            return;
          }
          const pixel = this.map.getEventPixel(evt.originalEvent);
          const hit = this.map.hasFeatureAtPixel(pixel, {
            layerFilter: (layer) => layer !== this.drawLayer
          });
          this.map.getTargetElement().style.cursor = hit ? 'pointer' : '';
        });

        console.log('地图初始化完成');
      } catch (error) {
        console.error('地图初始化失败:', error);
      }
    },
    // 中心定位
    getInitialCenter () {
      return storage.get(LonLat) || [113.32446, 23.10647];
    },
    // 切换全屏
    toggleFullScreen () {
      const element = this.$el;
      if (!this.isFullScreen) {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    },
    // 处理全屏状态变化
    handleFullScreenChange () {
      this.isFullScreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      // 全屏切换后通知地图更新尺寸
      setTimeout(() => {
        if (this.map) {
          this.map.updateSize();
        }
      }, 300);
    },
    // 处理图层切换
    handleLayerChange (value) {
      this.selectedValue = value;
      if (this.baseLayer) {
        this.map.removeLayer(this.baseLayer);
      }
      this.createBaseLayer();
    },
    // 加载底图
    createBaseLayer () {
      const config = this.mapLayerConfigs[this.selectedValue];
      let projection = config.projection || 'EPSG:3857';

      if (projection === 'GCJ-02') {
        projection = registerGCJ02();
      }

      const sourceOptions = {
        projection,
        crossOrigin: 'anonymous',
        maxZoom: config.maxZoom,
        wrapX: true,
        cacheSize: 2048,
        transition: 0,
        interpolate: false,
        tileSize: 256,
        tilePixelRatio: 1,
        reprojectionErrorThreshold: 0.5
      };

      if (config.useTileUrlFunction) {
        sourceOptions.tileUrlFunction = (coord) => getSpecialTileUrl(coord[0], coord[1], coord[2], config.url, config.urlEnd, config.name);
      } else {
        sourceOptions.url = config.url;
      }

      this.baseLayer = new TileLayer({
        properties: { name: config.name, title: '地图图层', isBaseLayer: true },
        preload: 1,
        source: new XYZ(sourceOptions),
        zIndex: 0
      });

      this.map.getLayers().insertAt(0, this.baseLayer);
    },
    // 移除原有的 getVETileUrl 方法 (已合并到 helper)
    // 获取或创建对应类型的 WebGL 图层 
    getOrCreateMarkerLayer (pointType) {
      if (this.markerLayers[pointType]) return this.markerLayers[pointType];

      const mapping = this.mappingTable[pointType];
      // 直接使用图标地址，移除保底和本地 require 逻辑
      let iconUrl = mapping ? mapping.icon : '';

      // 如果是 HTTP/HTTPS URL，优先使用缓存的 Base64
      if (iconUrl && (iconUrl.startsWith('http://') || iconUrl.startsWith('https://'))) {
        const cachedBase64 = getCachedImageBase64(iconUrl);
        if (cachedBase64 && cachedBase64 !== iconUrl) {
          // 使用缓存的 Base64，避免 HTTP 请求
          iconUrl = cachedBase64;
        }
      }

      // WebGLPointsLayer 样式
      const layer = new WebGLPointsLayer({
        source: new VectorSource(),
        zIndex: 10,
        visible: this.visibleLayers[pointType] !== false,
        style: {
          'icon-src': iconUrl,
          'icon-cross-origin': (iconUrl && iconUrl.startsWith('http') && !iconUrl.startsWith('data:image')) ? 'anonymous' : undefined,
          'icon-width': 24,
          'icon-height': 24,
          'icon-opacity': 1,
          'icon-rotate-with-view': false
        }
      });

      // 如果图标是 HTTP URL 且缓存中没有，异步预加载（用于后续更新）
      const originalIconUrl = mapping ? mapping.icon : '';
      if (originalIconUrl && originalIconUrl.startsWith('http') && !getCachedImageBase64(originalIconUrl)) {
        getImageBase64(originalIconUrl).then(cachedBase64 => {
          // 如果图层已创建且缓存成功，更新图层样式使用 Base64
          if (cachedBase64 && cachedBase64 !== originalIconUrl && this.markerLayers[pointType]) {
            this.markerLayers[pointType].setStyle({
              'icon-src': cachedBase64,
              'icon-cross-origin': undefined,
              'icon-width': 24,
              'icon-height': 24,
              'icon-opacity': 1,
              'icon-rotate-with-view': false
            });
          }
        }).catch(() => { });
      }

      this.markerLayers[pointType] = layer;
      this.markerSources[pointType] = layer.getSource();
      this.map.addLayer(layer);
      return layer;
    },
    // 获取点位数据
    getMarkerData () {
      return this.$http.get(this.markerUrl).then(res => {
        this.addMarkersToMap(res.data);
      });
    },
    // 添加标记点到地图
    addMarkersToMap (data) {
      if (!data || !Array.isArray(data)) return;

      Object.values(this.markerSources).forEach(s => s.clear());
      this.markerDataGroups = {};

      data.forEach(item => {
        if (!item.point || !this.mappingTable[item.point]) return;

        try {
          const rawCoords = typeof item.coordinates === 'string' ? JSON.parse(item.coordinates) : item.coordinates;
          if (!Array.isArray(rawCoords)) return;

          const feature = new Feature({
            geometry: new Point(fromLonLat(rawCoords)),
            ...item
          });
          feature.setId(item.id);

          const type = item.point;
          if (!this.markerDataGroups[type]) this.markerDataGroups[type] = [];
          this.markerDataGroups[type].push(feature);

          if (this.visibleLayers[type]) {
            this.getOrCreateMarkerLayer(type).getSource().addFeature(feature);
          }
        } catch (e) { console.error('解析点位失败:', item.id, e); }
      });

      this.map.render();
    },
    // 获取管线数据
    getLineData () {
      return this.$http.get(this.lineUrl).then(res => {
        this.addLinesToMap(res.data);
      });
    },
    // 添加管线到地图
    addLinesToMap (data) {
      if (!this.lineSource || !Array.isArray(data)) return;
      this.lineSource.clear();

      const features = data.map(item => {
        try {
          const coords = typeof item.coordinates === 'string' ? JSON.parse(item.coordinates) : item.coordinates;
          if (Array.isArray(coords) && coords.length >= 2) {
            const feature = new Feature({
              geometry: new LineString(coords.map(c => fromLonLat(c))),
              ...item
            });
            feature.setId(item.id);
            return feature;
          }
        } catch (e) { console.warn('管线解析跳过:', item.id); }
        return null;
      }).filter(Boolean);

      this.lineSource.addFeatures(features);
    },
    // 创建管线图层
    createLineLayer () {
      this.lineSource = new VectorSource();
      // 使用 WebGLVectorLayer 提供高性能线渲染 (OpenLayers 7.0+)
      this.lineLayer = new WebGLVectorLayer({
        source: this.lineSource,
        zIndex: 1,
        style: this.getLineStyle()
      });
      this.map.addLayer(this.lineLayer);
    },
    // 获取线图层样式（动态配置）
    getLineStyle () {
      // 从 mappingTable 中找出所有线类型的配置
      const lineTypes = [];
      Object.keys(this.mappingTable).forEach(legendName => {
        const config = this.mappingTable[legendName];
        if (config && config.type === 'line') {
          // 解析样式值（支持大小写不敏感）
          const styleValue = config.styleValue || '';
          const colorMatch = styleValue.match(/strokecolor:([^;]+)/i);
          const widthMatch = styleValue.match(/strokewidth:([^;]+)/i);

          const color = colorMatch ? colorMatch[1].trim() : '#409EFF';
          const width = widthMatch ? parseInt(widthMatch[1].trim()) || 3 : 3;

          lineTypes.push({
            legendName,
            color,
            width,
            visible: this.visibleLayers[legendName] !== false
          });
        }
      });

      // 如果没有线类型配置，返回默认样式
      if (lineTypes.length === 0) {
        return {
          'stroke-color': '#409EFF',
          'stroke-width': 3
        };
      }

      // 动态构建 stroke-color 表达式
      const colorCases = [];
      lineTypes.forEach(({ legendName, color, visible }) => {
        colorCases.push(['==', ['get', 'voltageType'], legendName]);
        colorCases.push(visible ? color : 'rgba(0,0,0,0)');
      });
      colorCases.push('#409EFF'); // 默认颜色

      // 动态构建 stroke-width 表达式
      const widthCases = [];
      lineTypes.forEach(({ legendName, width, visible }) => {
        widthCases.push(['==', ['get', 'voltageType'], legendName]);
        widthCases.push(visible ? width : 0);
      });
      widthCases.push(3); // 默认宽度

      return {
        'stroke-color': ['case', ...colorCases],
        'stroke-width': ['case', ...widthCases]
      };
    },
    // 更新线图层样式
    updateLineLayerStyle () {
      if (this.lineLayer) {
        this.lineLayer.setStyle(this.getLineStyle());
      }
    },
    // 切换图层显示隐藏
    handleLayerVisibilityChange ({ type, isVisible }) {
      this.$set(this.visibleLayers, type, isVisible);
      this.clearHighlight(); // 切换图层时取消选中样式

      // 如果处于新增模式，切换图层不应重置新增状态或关闭面板
      if (!this.isAdding) {
        this.clearEditHighlight(); // 切换图层时清除编辑样式

        // 切换图层时关闭详情面板和编辑面板
        if (this.$refs.infoPanel) {
          this.$refs.infoPanel.close();
        }
        if (this.$refs.editForm) {
          this.$refs.editForm.close();
        }
      }

      // 处理线图层显示隐藏（动态判断）
      // 如果当前切换的类型是线类型（type === 'line'），则更新线图层样式
      const mappingConfig = this.mappingTable[type];
      if (mappingConfig && mappingConfig.type === 'line') {
        this.updateLineLayerStyle();
        return;
      }

      let layer = this.markerLayers[type];

      // 如果变为可见，优先使用缓存数据，不重新请求
      if (isVisible) {
        // 如果图层不存在，先创建
        if (!layer) {
          layer = this.getOrCreateMarkerLayer(type);
        }

        // 如果图层存在但数据为空，且缓存中有数据，则从缓存加载
        const source = layer.getSource();
        if (source.getFeatures().length === 0 && this.markerDataGroups[type] && this.markerDataGroups[type].length > 0) {
          console.log(`从缓存加载图层数据: ${type}，共 ${this.markerDataGroups[type].length} 个要素`);
          source.addFeatures(this.markerDataGroups[type]);
        }
      }

      // 设置图层可见性
      if (layer) {
        layer.setVisible(isVisible);
      }
    },
    /**
     * 打开编辑表单
     * @param {Object} data - 要素详情数据
     */
    handleEdit (data) {
      console.log('EditingAmap 收到编辑请求:', data);

      // 如果处于移动模式或新增模式，先退出
      this.isMovingPoint = false;
      if (this.drawSource) this.drawSource.clear();

      if (this.isAdding) {
        this.isAdding = false;
        this.clearDraw();
      }

      const type = this.$refs.infoPanel.currentType;
      this.$refs.editForm.show(data, type);

      // 进入编辑状态，添加蓝色高亮
      if (this.selectedFeature) {
        this.addEditHighlight(this.selectedFeature);
      }
    },

    /**
     * 处理详情面板关闭
     */
    handleInfoPanelClose () {
      this.clearHighlight();
      // 无论是否在新增模式，只要关闭详情框，移动现有点的模式必须取消
      this.isMovingPoint = false;
      this.moveConfirmVisible = false;
      if (this.moveOverlay) this.moveOverlay.setPosition(undefined);

      // 仅在非新增模式下才清除 drawSource，以保护新增时的黄点黄线
      if (!this.isAdding && !this.isLineAdding) {
        if (this.drawSource) this.drawSource.clear();
      }
    },

    /**
     * 处理详情面板发出的移动点请求 (开始)
     */
    handleMovePointStart (data) {
      console.log('EditingAmap 收到移动点请求:', data);

      // 1. 取消可能存在的新增操作
      this.isAdding = false;
      this.isLineAdding = false;
      this.lineAddPoints = [];
      if (this.drawSource) this.drawSource.clear();

      // 2. 如果编辑表单打开着，关闭它
      if (this.$refs.editForm) {
        this.$refs.editForm.close();
      }

      this.isMovingPoint = true;
      this.$message.info('请在地图上点击新位置');
    },

    /**
     * 取消移动点
     */
    handleMovePointCancel () {
      this.isMovingPoint = false;
      this.moveConfirmVisible = false;
      if (this.moveOverlay) {
        this.moveOverlay.setPosition(undefined);
      }
      if (this.drawSource) {
        this.drawSource.clear();
      }
    },

    /**
     * 处理详情面板发出的移动取消请求
     */
    handleInfoPanelMoveCancel () {
      // 逻辑与 handleInfoPanelClose 保持一致：新增模式下不清除绘制，但必须取消移动状态
      this.isMovingPoint = false;
      this.moveConfirmVisible = false;
      if (this.moveOverlay) this.moveOverlay.setPosition(undefined);

      if (!this.isAdding && !this.isLineAdding) {
        this.handleMovePointCancel();
      }
    },

    /**
     * 处理详情面板发出的删除请求
     * @param {Object} data - 要素详情数据
     */
    handleDelete (data) {
      const _this = this;
      this.isMovingPoint = false;
      if (this.drawSource) this.drawSource.clear();

      // 点击删除时，先关闭编辑表单
      if (this.$refs.editForm) {
        this.$refs.editForm.close();
      }

      const type = this.$refs.infoPanel.currentType;
      const id = data.id;

      this.$confirm({
        title: '确认删除',
        content: `确定要删除该${type === 'gis_pipe_line' ? '管线' : '管网'}吗？`,
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        getContainer: () => this.$el,
        onOk () {
          console.log('执行删除:', id, '类型:', type);
          _this.handleFormLoading(true, '正在删除...');

          const isLine = type === 'gis_pipe_line';
          const url = isLine ? _this.lineDeleteUrl : _this.pointDeleteUrl;

          _this.$http.post(url, data).then(res => {
            if (res.success || res.code === 200 || res.code === 0) {
              _this.$message.success('删除成功');

              // 如果后端返回了指令，则执行指令更新地图
              if (res.data && res.data.typeSet) {
                _this.handleEditSave(null, res.data);
              } else {
                // 兜底逻辑：如果没返回指令，则手动根据类型执行删除 (这里复用 updateHandler 的逻辑)
                const instruction = isLine ? 'DEL_LINE' : 'DEL_POINT';
                const resData = isLine ? { lineList: [{ id }] } : { pointList: [{ id }] };
                if (strategyMap[instruction]) {
                  strategyMap[instruction](_this, resData);
                }
                _this.postUpdateCleanup();
              }
            } else {
              _this.$message.error(res.message || '删除失败');
            }
          }).catch(err => {
            console.error('删除请求失败:', err);
            _this.$message.error('系统异常，删除失败');
          }).finally(() => {
            _this.handleFormLoading(false);
          });
        }
      });
    },

    /**
     * 编辑保存后的回调
     * @param {Object} formData - 表单数据
     * @param {Object} resData - 接口返回 data
     * @param {string} [saveType] - 要素类型，来自 EditForm.currentType（如 gis_pipe_line）
     */
    handleEditSave (formData, resData, saveType) {
      console.log('保存成功，同步更新地图数据:', formData, resData);

      // 只有在后端返回了明确更新指令时才执行操作
      if (resData && resData.typeSet && Array.isArray(resData.typeSet)) {
        resData.typeSet.forEach(type => {
          if (strategyMap[type]) {
            strategyMap[type](this, resData);
          }
        });

        this.postUpdateCleanup();
      }

      // 管线编辑/新增成功后重新请求详情，刷新右侧「详情」面板
      if (saveType && saveType.startsWith('gis_pipe_line')) {
        const lineId = formData && formData.id
          ? formData.id
          : (resData && resData.lineList && resData.lineList[0] && resData.lineList[0].id);
        if (lineId) {
          this.fetchLineDetail(String(lineId), { silent: true });
        }
      }
    },
    // 处理表单保存时的加载状态
    handleFormLoading (isLoading, tip) {
      this.loading = isLoading;
      this.loadingTip = tip || '正在处理...';
    },

    /**
     * 更新完成后清理工作 (清除高亮、关闭面板)
     */
    postUpdateCleanup () {
      this.clearHighlight();
      this.clearEditHighlight();
      this.clearDraw();
      if (this.$refs.infoPanel) this.$refs.infoPanel.close();
      if (this.$refs.editForm) this.$refs.editForm.close();
    },

    /**
     * 本地更新当前选中要素的属性 (兜底逻辑)
     */
    updateCurrentFeatureProperties (formData) {
      if (!this.selectedFeature) return;

      Object.keys(formData).forEach(key => {
        this.selectedFeature.set(key, formData[key]);
      });

      const geometryType = this.selectedFeature.getGeometry() ? this.selectedFeature.getGeometry().getType() : null;
      if (geometryType === 'LineString') {
        this.updateLineLayerStyle();
      }

      if (this.$refs.infoPanel && this.$refs.infoPanel.visible) {
        const type = this.$refs.infoPanel.currentType;
        this.$refs.infoPanel.show(this.selectedFeature.getProperties(), type);
      }
    },
    // 添加单个点位要素到地图
    addSingleMarker (item) {
      if (!item.point || !this.mappingTable[item.point]) return;

      const type = item.point;
      try {
        const coords = typeof item.coordinates === 'string' ? JSON.parse(item.coordinates) : item.coordinates;
        if (coords && Array.isArray(coords)) {
          const feature = new Feature({
            geometry: new Point(fromLonLat(coords)),
            ...item,
          });
          feature.setId(String(item.id)); // 确保 ID 为字符串

          // 1. 添加到缓存，确保未来可能触发的重新加载能包含此点
          if (!this.markerDataGroups[type]) this.markerDataGroups[type] = [];
          this.markerDataGroups[type].push(feature);

          // 2. 如果图层已经存在（无论当前是否可见），直接添加到 Source 中
          // 这样当图层从隐藏变为显示时，数据已经是同步的
          const source = this.markerSources[type];
          if (source) {
            source.addFeature(feature);
            source.changed(); // 强制触发重绘
          } else if (this.visibleLayers[type]) {
            // 如果图层不存在但配置为可见（理论上不应发生，作为兜底），创建并添加
            this.getOrCreateMarkerLayer(type).getSource().addFeature(feature);
          }
        }
      } catch (e) {
        console.error('解析或添加单个点位失败:', e, item);
      }
    },
    // 添加单个线位要素到地图
    addSingleLine (item) {
      if (!this.lineSource) return;
      try {
        const coords = typeof item.coordinates === 'string' ? JSON.parse(item.coordinates) : item.coordinates;
        if (coords && Array.isArray(coords) && coords.length >= 2) {
          const transformedCoords = coords.map(coord => fromLonLat(coord));
          const feature = new Feature({
            geometry: new LineString(transformedCoords),
            ...item
          });
          feature.setId(String(item.id)); // 确保 ID 为字符串
          this.lineSource.addFeature(feature);
          this.lineSource.changed(); // 强制触发重绘
        }
      } catch (e) {
        console.error('解析或添加单个线位失败:', e, item);
      }
    },
    // 创建高亮图层
    createHighlightLayer () {
      this.highlightSource = new VectorSource();
      this.highlightLayer = new VectorLayer({
        source: this.highlightSource,
        zIndex: 100, // 确保在最上层
        style: (feature) => {
          const geometryType = feature.getGeometry().getType();
          if (geometryType === 'LineString') {
            return new Style({
              stroke: new Stroke({
                color: '#ff0000',
                width: 7
              })
            });
          } else if (geometryType === 'Point') {
            return new Style({
              image: new CircleStyle({
                radius: 8,
                fill: new Fill({ color: '#ff0000' }), // 红色实心
                stroke: new Stroke({ color: '#ffffff', width: 4 }) // 白色圈
              })
            });
          }
        }
      });
      this.map.addLayer(this.highlightLayer);
    },
    // 添加高亮要素
    addHighlight (feature) {
      if (!this.highlightSource || !feature) return;
      this.highlightSource.clear(); // 同时只存在一个高亮

      const highlightFeature = new Feature({
        geometry: feature.getGeometry().clone()
      });
      this.highlightSource.addFeature(highlightFeature);
    },
    // 清除选中高亮
    clearHighlight () {
      if (this.highlightSource) {
        this.highlightSource.clear();
      }
      this.selectedFeature = null;
    },

    /**
     * 创建编辑状态的高亮图层 (蓝色)
     */
    createEditHighlightLayer () {
      this.editHighlightSource = new VectorSource();
      this.editHighlightLayer = new VectorLayer({
        source: this.editHighlightSource,
        zIndex: 101, // 确保在普通高亮层之上
        style: new Style({
          stroke: new Stroke({
            color: '#0000ff', // 蓝色
            width: 7
          }),
          image: new CircleStyle({
            radius: 8,
            fill: new Fill({ color: '#0000ff' }), // 蓝色实心
            stroke: new Stroke({ color: '#ffffff', width: 4 }) // 白色圈
          })
        })
      });
      this.map.addLayer(this.editHighlightLayer);
    },

    /**
     * 添加编辑状态高亮
     */
    addEditHighlight (feature) {
      if (!this.editHighlightSource || !feature) return;
      this.editHighlightSource.clear();
      const editFeature = new Feature({
        geometry: feature.getGeometry().clone()
      });
      this.editHighlightSource.addFeature(editFeature);
    },

    /**
     * 清除编辑状态高亮
     */
    clearEditHighlight () {
      if (this.editHighlightSource) {
        this.editHighlightSource.clear();
      }
      // 当编辑面板关闭时（无论是取消还是保存），确保清理绘制状态并退出新增模式
      if (this.drawSource) {
        this.drawSource.clear();
      }
      this.isAdding = false;
      this.isLineAdding = false;
      // 移动点状态由专门的 handleMovePointCancel 处理，此处不再重置
      this.lineAddPoints = [];
    },

    /**
     * 处理新增物探点
     */
    handleAddPoint () {
      if (!this.isAdding) {
        // 1. 进入新增前，必须清理移动模式和量测模式
        this.handleMovePointCancel();
        this.isMeasuring = false;
        this.isMeasuringSurface = false;
        this.clearMeasureDistance();
        this.clearMeasureSurface();

        // 2. 清除之前的绘制和连线状态
        if (this.drawSource) this.drawSource.clear();
        this.lineAddPoints = [];

        this.isAdding = true;
        this.isLineAdding = false; // 互斥
        this.addModeType = 'add'; // 默认进入新增选点模式
        this.$message.info('请在地图上点击位置以新增物探点');
        this.clearHighlight();
        // 手动清理编辑高亮，不直接调用 clearEditHighlight 避免 isAdding 被重置
        if (this.editHighlightSource) this.editHighlightSource.clear();
        if (this.$refs.infoPanel) this.$refs.infoPanel.close();

        // 显示空表单，标记为新增模式
        this.$refs.editForm.show({}, 'gis_pipe_network');
      } else {
        this.isAdding = false;
        this.clearDraw();
        this.$refs.editForm.close();
      }
    },

    /**
     * 处理新增管线 (连线模式)
     */
    handleAddLine () {
      if (!this.isLineAdding) {
        // 1. 进入新增前，必须清理移动模式和量测模式
        this.handleMovePointCancel();
        this.isMeasuring = false;
        this.isMeasuringSurface = false;
        this.clearMeasureDistance();
        this.clearMeasureSurface();

        // 2. 清除之前的绘制和连线状态
        if (this.drawSource) this.drawSource.clear();
        this.lineAddPoints = [];

        this.isLineAdding = true;
        this.isAdding = false; // 互斥
        this.addModeType = 'add';
        this.$message.info('请依次点击地图上的两个点以建立连线');
        this.clearHighlight();
        if (this.editHighlightSource) this.editHighlightSource.clear();
        if (this.$refs.infoPanel) this.$refs.infoPanel.close();

        this.$refs.editForm.show({}, 'gis_pipe_line');
      } else {
        this.isLineAdding = false;
        this.clearDraw();
        this.$refs.editForm.close();
      }
    },

    /**
     * 创建绘制图层 (用于新增时的位置预览)
     */
    createDrawLayer () {
      this.drawSource = new VectorSource();
      this.drawLayer = new VectorLayer({
        source: this.drawSource,
        zIndex: 102,
        style: [
          new Style({
            stroke: new Stroke({
              color: '#fadb14',
              width: 4
            }),
            image: new CircleStyle({
              radius: 8,
              fill: new Fill({ color: '#fadb14' }), // 黄色
              stroke: new Stroke({ color: '#ffffff', width: 4 }) // 白色圈
            })
          })
        ]
      });
      this.map.addLayer(this.drawLayer);
    },

    /**
     * 清除绘制
     */
    clearDraw () {
      if (this.drawSource) {
        this.drawSource.clear();
      }
      this.isAdding = false;
      this.isLineAdding = false;
      this.isMovingPoint = false;
      this.lineAddPoints = [];
    },

    /**
     * 重新绘制连线预览
     */
    redrawLinePreview () {
      if (!this.drawSource) return;
      this.drawSource.clear();

      this.lineAddPoints.forEach(p => {
        const feature = new Feature({
          geometry: new Point(p.coordinates)
        });
        this.drawSource.addFeature(feature);
      });

      if (this.lineAddPoints.length === 2) {
        const lineFeature = new Feature({
          geometry: new LineString([
            this.lineAddPoints[0].coordinates,
            this.lineAddPoints[1].coordinates
          ])
        });
        this.drawSource.addFeature(lineFeature);
      }
    },

    // --- 测距功能开始 (参考 gisMap.vue) ---
    /**
     * 初始化测距图层
     */
    initMeasureDistanceLayer () {
      this.measureDistanceSource = new VectorSource();
      this.measureDistanceLayer = new VectorLayer({
        source: this.measureDistanceSource,
        zIndex: 1002,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'measure-point') {
            return new Style({
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({ color: '#ff4757' }),
                stroke: new Stroke({ color: '#ffffff', width: 2 })
              })
            });
          } else if (type === 'measure-line') {
            return new Style({
              stroke: new Stroke({
                color: '#ff4757',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'measure-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '14px Arial',
                fill: new Fill({ color: '#ff4757' }),
                stroke: new Stroke({ color: '#ffffff', width: 2 }),
                offsetY: -15
              })
            });
          } else if (type === 'measure-total-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: 'bold 14px Arial',
                fill: new Fill({ color: '#ff4757' }),
                stroke: new Stroke({ color: '#ffffff', width: 3 }),
                offsetY: 20
              })
            });
          }
        }
      });
      this.map.addLayer(this.measureDistanceLayer);
    },

    /**
     * 切换测距功能状态
     */
    handleToggleMeasure () {
      this.isMeasuring = !this.isMeasuring;
      if (this.isMeasuring) {
        // 开启测距前清理其他模式
        this.isAdding = false;
        this.isLineAdding = false;
        this.isMovingPoint = false;
        this.isMeasuringSurface = false; // 关闭测面状态
        this.clearMeasureSurface(); // 清除测面数据
        if (this.drawSource) this.drawSource.clear();
        if (this.$refs.editForm) this.$refs.editForm.close(); // 关闭新增/编辑表单
        this.$message.info('测距已开启，请在地图上点击取点');
      } else {
        this.clearMeasureDistance();
      }
    },

    /**
     * 测距逻辑实现 (核心复刻 gisMap.vue)
     */
    measureDistance (coordinate) {
      // 添加测距点坐标
      this.measureDistancePoints.push(coordinate);

      // 1. 创建测距点要素
      const pointFeature = new Feature({
        geometry: new Point(coordinate),
        type: 'measure-point',
        index: this.measureDistancePoints.length - 1
      });
      this.measureDistanceSource.addFeature(pointFeature);

      // 2. 如果有多个点，创建线段
      if (this.measureDistancePoints.length > 1) {
        const lineCoordinates = this.measureDistancePoints.slice(-2);
        const lineFeature = new Feature({
          geometry: new LineString(lineCoordinates),
          type: 'measure-line',
          index: this.measureDistancePoints.length - 2
        });
        this.measureDistanceSource.addFeature(lineFeature);

        // 3. 计算并显示本段距离 (使用 geoUtils)
        // 注意：gisMap 中坐标可能没转换，但 EditingAmap 默认是 3857 投影，
        // 这里需要参考 gisMap 是否对坐标做了 toLonLat 转换后再计算
        const p1 = olProj.toLonLat(lineCoordinates[0]);
        const p2 = olProj.toLonLat(lineCoordinates[1]);
        const distance = calculateDistance(p1, p2);
        this.showDistanceLabel(coordinate, distance);

        // 4. 计算并显示总距离
        if (this.measureDistanceTotalLabel) {
          this.measureDistanceSource.removeFeature(this.measureDistanceTotalLabel);
          this.measureDistanceTotalLabel = null;
        }
        const lonLatPoints = this.measureDistancePoints.map(p => olProj.toLonLat(p));
        const totalDistance = calculateTotalDistance(lonLatPoints);
        this.showTotalDistanceLabel(coordinate, totalDistance);
      }
    },

    /**
     * 显示单段距离标注
     */
    showDistanceLabel (coordinate, distance) {
      const distanceText = formatDistance(distance);
      const labelFeature = new Feature({
        geometry: new Point(coordinate),
        type: 'measure-label',
        text: distanceText
      });
      this.measureDistanceSource.addFeature(labelFeature);
    },

    /**
     * 显示总距离标注
     */
    showTotalDistanceLabel (coordinate, totalDistance) {
      const totalDistanceText = '总距离: ' + formatDistance(totalDistance);
      const labelFeature = new Feature({
        geometry: new Point(coordinate),
        type: 'measure-total-label',
        text: totalDistanceText
      });
      this.measureDistanceSource.addFeature(labelFeature);
      this.measureDistanceTotalLabel = labelFeature;
    },

    /**
     * 清除测距数据
     */
    clearMeasureDistance () {
      this.measureDistancePoints = [];
      this.measureDistanceTotalLabel = null;
      if (this.measureDistanceSource) {
        this.measureDistanceSource.clear();
      }
    },

    /**
     * 初始化测面图层
     */
    initMeasureSurfaceLayer () {
      this.measureSurfaceSource = new VectorSource();
      this.measureSurfaceLayer = new VectorLayer({
        source: this.measureSurfaceSource,
        zIndex: 1003,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'measure-surface-point') {
            return new Style({
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({ color: '#ff4d4f' }),
                stroke: new Stroke({ color: '#ffffff', width: 2 })
              })
            });
          } else if (type === 'measure-surface-line') {
            return new Style({
              stroke: new Stroke({
                color: '#ff4d4f',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'measure-surface-polygon') {
            return new Style({
              fill: new Fill({ color: 'rgba(255, 77, 79, 0.2)' }),
              stroke: new Stroke({ color: '#ff4d4f', width: 2 })
            });
          } else if (type === 'measure-surface-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '14px Arial',
                fill: new Fill({ color: '#ff4d4f' }),
                stroke: new Stroke({ color: '#ffffff', width: 2 }),
                offsetY: -15
              })
            });
          }
        }
      });
      this.map.addLayer(this.measureSurfaceLayer);
    },

    /**
     * 切换测面功能状态
     */
    handleToggleMeasureSurface () {
      this.isMeasuringSurface = !this.isMeasuringSurface;
      if (this.isMeasuringSurface) {
        // 开启测面前清理其他模式
        this.isAdding = false;
        this.isLineAdding = false;
        this.isMovingPoint = false;
        this.isMeasuring = false;
        this.clearMeasureDistance(); // 清除测距数据
        if (this.drawSource) this.drawSource.clear();
        if (this.$refs.editForm) this.$refs.editForm.close(); // 关闭新增/编辑表单
        this.$message.info('测面已开启，请在地图上点击取点');
      } else {
        this.clearMeasureSurface();
      }
    },

    /**
     * 测面逻辑实现 (参考 gisMap.vue)
     */
    measureSurface (coordinate) {
      this.measureSurfacePoints.push(coordinate);

      // 1. 创建点要素
      const pointFeature = new Feature({
        geometry: new Point(coordinate),
        type: 'measure-surface-point',
        index: this.measureSurfacePoints.length - 1
      });
      this.measureSurfaceSource.addFeature(pointFeature);

      // 2. 如果有多个点，创建线段
      if (this.measureSurfacePoints.length > 1) {
        const lineCoordinates = this.measureSurfacePoints.slice(-2);
        const lineFeature = new Feature({
          geometry: new LineString(lineCoordinates),
          type: 'measure-surface-line',
          index: this.measureSurfacePoints.length - 2
        });
        this.measureSurfaceSource.addFeature(lineFeature);

        // 3. 如果有3个或更多点，更新多边形和面积
        if (this.measureSurfacePoints.length >= 3) {
          this.updateSurfacePolygon();
        }
      }
    },

    /**
     * 更新测面多边形和面积标注
     */
    updateSurfacePolygon () {
      // 移除之前的多边形
      if (this.measureSurfacePolygon) {
        this.measureSurfaceSource.removeFeature(this.measureSurfacePolygon);
      }

      // 创建闭合多边形坐标
      const polygonCoordinates = [...this.measureSurfacePoints, this.measureSurfacePoints[0]];
      const polygonFeature = new Feature({
        geometry: new Polygon([polygonCoordinates]),
        type: 'measure-surface-polygon'
      });
      this.measureSurfaceSource.addFeature(polygonFeature);
      this.measureSurfacePolygon = polygonFeature;

      // 计算面积 (使用 geoUtils)
      const lonLatPoints = this.measureSurfacePoints.map(p => olProj.toLonLat(p));
      const area = calculatePolygonArea(lonLatPoints);
      const interiorPoint = polygonFeature.getGeometry().getInteriorPoint();
      const labelCoordinate = interiorPoint ? interiorPoint.getCoordinates() : this.measureSurfacePoints[0];
      this.showSurfaceLabel(labelCoordinate, area);
    },

    /**
     * 显示面积标注
     */
    showSurfaceLabel (coordinate, area) {
      if (this.measureSurfaceLabel) {
        this.measureSurfaceSource.removeFeature(this.measureSurfaceLabel);
      }
      const areaText = formatArea(area);
      const labelFeature = new Feature({
        geometry: new Point(coordinate),
        type: 'measure-surface-label',
        text: areaText
      });
      this.measureSurfaceSource.addFeature(labelFeature);
      this.measureSurfaceLabel = labelFeature;
    },

    /**
     * 清除测面数据
     */
    clearMeasureSurface () {
      this.measureSurfacePoints = [];
      this.measureSurfacePolygon = null;
      this.measureSurfaceLabel = null;
      if (this.measureSurfaceSource) {
        this.measureSurfaceSource.clear();
      }
    },

    /**
     * 处理搜索结果选择
     */
    handleSearchResultSelect (item) {
      if (!item.feature) return;

      const geometry = item.feature.getGeometry();
      const coords = geometry.getCoordinates();

      // 1. 视角定位
      if (item.type === 'Point') {
        this.map.getView().animate({
          center: coords,
          zoom: 18,
          duration: 500
        });
      } else if (item.type === 'LineString') {
        this.map.getView().fit(geometry.getExtent(), {
          padding: [100, 100, 100, 100],
          duration: 500,
          maxZoom: 18
        });
      }

      // 2. 模拟点击拾取，弹出详情
      this.handleFeaturePick({
        pixel: this.map.getPixelFromCoordinate(item.type === 'Point' ? coords : geometry.getClosestPoint(coords)),
        coordinate: coords
      }, item.feature);
    },
    // --- 测距/测面功能结束 ---

    /**
     * 初始化移动点确认弹窗 Overlay
     */
    initMoveOverlay () {
      this.moveOverlay = new Overlay({
        element: this.$refs.movePopup,
        autoPan: {
          animation: {
            duration: 250,
          },
        },
        offset: [0, -20], // 向上偏移 12 像素，留出小三角和点位的间隙
        positioning: 'bottom-center'
      });
      this.map.addOverlay(this.moveOverlay);
    },

    handleMapClick (evt) {
      // 0. 测距/测面模式处理 (参考 gisMap.vue)
      if (this.isMeasuring) {
        this.measureDistance(evt.coordinate);
        return;
      }
      if (this.isMeasuringSurface) {
        this.measureSurface(evt.coordinate);
        return;
      }

      // 1. 探测绘制层 (黄点)
      const drawFeature = this.map.forEachFeatureAtPixel(evt.pixel, f => f, { layerFilter: l => l === this.drawLayer });
      if (drawFeature) {
        console.log('[预览点位置]', olProj.toLonLat(drawFeature.getGeometry().getCoordinates()));
      }

      const coords = olProj.toLonLat(evt.coordinate);

      // 2. 状态模式处理
      if (this.isLineAdding && this.addModeType === 'add') {
        this.handleLineAddingClick(evt);
        return;
      }

      if (this.isAdding && this.addModeType === 'add') {
        this.handleNewPointClick(coords, evt.coordinate);
        return;
      }

      if (this.isMovingPoint) {
        this.handleMovePointClick(coords, evt.coordinate);
        return;
      }

      // 3. 普通点击拾取
      this.handleFeaturePick(evt);
    },

    // 抽离具体的点击逻辑
    handleNewPointClick (lonLat, mapCoords) {
      this.drawSource.clear();
      this.drawSource.addFeature(new Feature({ geometry: new Point(mapCoords) }));

      this.$http.post(this.pointConverterUrl, lonLat).then(res => {
        const formData = { ...res.data };
        if (res.data?.x !== undefined) formData.point_x = res.data.x;
        if (res.data?.y !== undefined) formData.point_y = res.data.y;

        this.$refs.editForm.visible ? this.$refs.editForm.updateFields(formData) : this.$refs.editForm.show(formData, 'gis_pipe_network');
      }).catch(() => this.$refs.editForm.updateCoordinates(lonLat));
    },

    handleMovePointClick (lonLat, mapCoords) {
      this.drawSource.clear();
      this.drawSource.addFeature(new Feature({ geometry: new Point(mapCoords) }));
      this.executeMovePoint(lonLat, mapCoords);
    },

    /**
     * 抽离拾取要素逻辑
     * @param {Object} evt - 点击事件对象或模拟对象
     * @param {Feature} providedFeature - 可选，直接提供要素
     */
    handleFeaturePick (evt, providedFeature = null) {
      const feature = providedFeature || this.map.forEachFeatureAtPixel(evt.pixel, f => f, {
        layerFilter: l => ![this.drawLayer, this.highlightLayer, this.editHighlightLayer, this.measureDistanceLayer, this.measureSurfaceLayer].includes(l)
      });

      if (!feature) {
        this.clearHighlight();
        this.$refs.infoPanel?.close();
        if (!this.isAdding && !this.isLineAdding) this.$refs.editForm?.close();
        return;
      }

      this.selectedFeature = feature;
      this.addHighlight(feature);

      const id = feature.get('id');
      const type = feature.getGeometry().getType();
      if (id) {
        type === 'LineString' ? this.fetchLineDetail(id) : this.fetchPointDetail(id);
      }
      this.$emit('feature-click', { id, properties: feature.getProperties(), feature });
    },

    handleLineAddingClick (evt) {
      const feature = this.map.forEachFeatureAtPixel(evt.pixel, f => f, {
        layerFilter: l => ![this.drawLayer, this.highlightLayer, this.editHighlightLayer].includes(l)
      });

      if (feature?.getGeometry().getType() === 'Point') {
        const id = feature.get('id');
        if (!id) return this.$message.warning('缺失 ID');

        const index = this.lineAddPoints.findIndex(p => p.id === id);
        if (index !== -1) {
          this.lineAddPoints.splice(index, 1);
          this.syncLineFormFields();
          this.redrawLinePreview();
          return;
        }

        if (this.lineAddPoints.length >= 2) return this.$message.warning('已选择起止点');

        this.handleFormLoading(true, '获取点详情...');
        this.$http.get(`${this.pointDetailUrl}${id}`).then(res => {
          const detail = res.data;
          this.lineAddPoints.push({
            id,
            classNo: detail.class_no || detail.classNo,
            geophysicalPointNumber: detail.geophysicalPointNumber,
            coordinates: feature.getGeometry().getCoordinates()
          });
          this.syncLineFormFields();
          this.redrawLinePreview();
        }).finally(() => this.handleFormLoading(false));
      }
    },

    syncLineFormFields () {
      const p1 = this.lineAddPoints[0] || {};
      const p2 = this.lineAddPoints[1] || {};
      this.$refs.editForm.updateFields({
        startClassNo: p1.classNo || '',
        startGeophysicalPointNumber: p1.geophysicalPointNumber || '',
        endClassNo: p2.classNo || '',
        endGeophysicalPointNumber: p2.geophysicalPointNumber || ''
      });
    },
    /**
     * 执行移动点逻辑
     */
    executeMovePoint (coords, pixelCoords) {
      const properties = this.$refs.infoPanel.currentProperties;
      if (!properties || !properties.id) {
        this.$message.error('无法获取当前点位信息');
        return;
      }
      this.tempMoveCoords = coords;
      this.moveConfirmVisible = true;
      if (this.moveOverlay) {
        this.moveOverlay.setPosition(pixelCoords);
      }
    },

    /**
     * 取消移动操作 (从弹窗触发)
     */
    cancelMoveAction () {
      this.moveConfirmVisible = false;
      if (this.moveOverlay) {
        this.moveOverlay.setPosition(undefined);
      }
      if (this.drawSource) {
        this.drawSource.clear();
      }
    },

    /**
     * 确认移动操作 (从弹窗触发)
     */
    confirmMoveAction () {
      const _this = this;
      const properties = this.$refs.infoPanel.currentProperties;
      const coords = this.tempMoveCoords;

      if (!properties || !properties.id || !coords) {
        this.$message.error('移动信息缺失');
        return;
      }

      this.moveConfirmVisible = false;
      if (this.moveOverlay) {
        this.moveOverlay.setPosition(undefined);
      }

      this.handleFormLoading(true, '正在移动点位...');

      // 1. 获取最新详情
      this.$http.get(`${this.pointDetailUrl}${properties.id}`).then(res => {
        const detailData = res.data;
        if (!detailData) throw new Error('获取详情失败');

        // 2. 请求坐标转换
        return _this.$http.post(_this.pointConverterUrl, [coords[0], coords[1]]).then(convRes => {
          const convData = convRes.data;

          // 3. 替换坐标并过滤字段，只提交 formConfig.gis_pipe_network 中配置的字段
          const allowedFields = formConfig.gis_pipe_network.map(f => f.id);
          const submitData = { id: properties.id };

          // 合并原始数据和新坐标
          const mergedData = {
            ...detailData,
            point_x: convData.x !== undefined ? convData.x : coords[0],
            point_y: convData.y !== undefined ? convData.y : coords[1]
          };

          // 仅保留配置中的字段
          allowedFields.forEach(fieldId => {
            if (mergedData[fieldId] !== undefined) {
              submitData[fieldId] = mergedData[fieldId];
            }
          });

          return _this.$http.post('/pipe-network/gis/pipe-networks/edit/point', submitData);
        });
      }).then(res => {
        if (res.success || res.code === 200 || res.code === 0) {
          _this.$message.success('移动成功');
          _this.isMovingPoint = false;
          if (_this.$refs.infoPanel) _this.$refs.infoPanel.isMovingPoint = false;

          // 同步更新地图
          _this.handleEditSave(null, res.data);
        } else {
          _this.$message.error(res.message || '移动失败');
        }
      }).catch(err => {
        console.error('移动点失败:', err);
        _this.$message.error('移动操作失败');
      }).finally(() => {
        _this.handleFormLoading(false);
      });
    },

    /**
     * 获取管线详情
     * @param {string|number} id - 管线 id
     * @param {Object} [options]
     * @param {boolean} [options.silent] - 为 true 时不弹出「已获取管线详情」提示（如保存后静默刷新）
     */
    fetchLineDetail (id, options = {}) {
      const silent = options.silent === true;
      this.$http.get(`${this.lineDetailUrl}${id}`).then(res => {
        if (res.success || res.code === 200 || res.data) {
          const detailData = res.data;
          console.log('获取到管线详情:', detailData);
          if (!silent) {
            this.$message.success(`已获取管线详情`);
          }
          // 显示详情面板
          this.$refs.infoPanel.show(detailData, 'gis_pipe_line');
          this.$emit('line-detail', detailData);
        }
      }).catch(err => {
        console.error('获取管线详情失败:', err);
        this.$message.error('获取管线详情失败');
      });
    },
    // 获取物探点详情
    fetchPointDetail (id) {
      this.$http.get(`${this.pointDetailUrl}${id}`).then(res => {
        if (res.success || res.code === 200 || res.data) {
          const detailData = res.data;
          console.log('获取到点位详情:', detailData);
          this.$message.success(`已获取点位详情`);
          // 显示详情面板
          this.$refs.infoPanel.show(detailData, 'gis_pipe_network');
          this.$emit('point-detail', detailData);
        }
      }).catch(err => {
        console.error('获取点位详情失败:', err);
        this.$message.error('获取点位详情失败');
      });
    },


  },
}
</script>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
  background: #f0f2f5;

  .ol-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: auto;
  }

  /* 确保 OpenLayers 视口透明 */
  :deep(.ol-viewport) {
    background: transparent !important;

    canvas {
      background: transparent !important;
    }
  }
}

.map-loading-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2001;
  backdrop-filter: blur(2px);
}

.add-mode-toggle {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  display: flex;
  align-items: center;

  /deep/ .ant-switch {
    min-width: 85px;
    height: 28px;
    line-height: 26px;
    background-color: #52c41a; // 查看模式背景色 (绿色)
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    // 修复圆球位置和大小
    &::after {
      width: 24px;
      height: 24px;
      top: 1px;
      left: 2px;
      margin-left: 0 !important;
      transform: none !important;
      transition: all 0.2s ease-in-out;
    }

    &.ant-switch-checked::after {
      left: calc(100% - 26px) !important; // 24px width + 2px spacing
      margin-left: 0 !important;
      transform: none !important;
    }

    &.ant-switch-checked {
      background-color: #1890ff; // 新增模式背景色 (蓝色)
    }

    .ant-switch-inner {
      font-size: 12px;
      font-weight: bold;
    }
  }
}

.move-popup {
  background: white;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #ebeef5;
  min-width: 180px;

  // 使用伪元素制作向下的小三角
  &::after {
    content: '';
    position: absolute;
    bottom: -6px; // 与 border-top 宽度一致
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid white;
  }

  // 增加一个边框阴影的小三角（可选，为了让三角也有边框效果）
  &::before {
    content: '';
    position: absolute;
    bottom: -7px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 7px solid #ebeef5;
  }

  .move-popup-content {
    .move-popup-text {
      margin-bottom: 12px;
      font-size: 14px;
      color: #333;
      font-weight: 500;
      text-align: center;
    }

    .move-popup-actions {
      display: flex;
      justify-content: center;
      gap: 12px;
    }
  }
}
</style>